import { inquiry } from "@platform/domain";
import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Money } from "../common/money.js";
import { PageQuery, page } from "../common/pagination.js";
import { Quantity } from "../common/quantity.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { QuoteSummary } from "./quote.routes.js";
import { Incoterm, TradeParties, TradeSide } from "./terms.js";
/** Inquiries (INQ-1, INQ-4, card 052): a buyer's request to one shop, with its own deal conversation (ADR 0020). */
export const InquiryStatus = z.enum(inquiry.states).meta({ id: "InquiryStatus" });
export const InquiryLineInput = z.object({
    productId: Id,
    skuId: Id.optional().describe("Required when the product has several options"),
    quantity: Quantity.describe("In the option's unit; at least its minimum order. A sample's is 1"),
    targetUnitPrice: Money.optional().describe("What the buyer hopes to pay per unit; one currency for the inquiry"),
    sample: z
        .boolean()
        .optional()
        .describe("A request for the product's sample (CAT-6, card 096): quantity 1, no option or target price; an inquiry is all samples or none"),
});
export const InquiryLine = z
    .object({
    id: Id,
    productId: Id,
    skuId: Id.nullable(),
    productName: LocalizedText,
    productSlug: z.string().nullable(),
    skuCode: z.string().nullable(),
    quantity: Quantity,
    unit: z.string(),
    targetUnitPrice: Money.nullable(),
    sample: z.boolean().describe("A sample request (card 096); its unit is `sample`"),
    samplePrice: Money.nullable().describe("The product's sample price when the buyer asked"),
})
    .meta({ id: "InquiryLine" });
const Parties = TradeParties;
export const InquiryView = z
    .object({
    id: Id,
    status: InquiryStatus,
    side: TradeSide.describe("The caller's side"),
    ...Parties,
    conversationId: Id.nullable().describe("The deal conversation (ADR 0020)"),
    lines: z.array(InquiryLine),
    incoterm: Incoterm.nullable(),
    destinationCountry: CountryCode.nullable(),
    targetDate: z.iso.date().nullable(),
    message: z.string().nullable(),
    declineReason: z.string().nullable().describe("The shop's reason"),
    declineCode: z
        .enum(["product_unavailable"])
        .nullable()
        .describe("Set when the platform closed it: a product in it stopped being on sale"),
    quotes: z.array(QuoteSummary).describe("Its quote versions, newest first (card 053)"),
    expiresAt: Timestamp,
    createdAt: Timestamp,
    version: z.number().int().positive().describe("Sent as `If-Match` to accept one of its quotes"),
    actions: z.array(z.enum(["quote", "decline", "withdraw"])).describe("What the caller may do now"),
})
    .meta({ id: "InquiryView" });
export const InquirySummary = z
    .object({
    id: Id,
    status: InquiryStatus,
    side: TradeSide,
    ...Parties,
    lineCount: z.number().int().positive(),
    firstProductName: LocalizedText,
    expiresAt: Timestamp,
    createdAt: Timestamp,
})
    .meta({ id: "InquirySummary" });
/** The inquiry as its card shows it in the deal conversation, read afresh for each viewer. */
export const InquiryCardData = z
    .object({
    status: InquiryStatus,
    ...Parties,
    lines: z.array(InquiryLine.pick({
        productName: true,
        skuCode: true,
        quantity: true,
        unit: true,
        targetUnitPrice: true,
    })),
    incoterm: Incoterm.nullable(),
    destinationCountry: CountryCode.nullable(),
    targetDate: z.iso.date().nullable(),
    expiresAt: Timestamp,
})
    .meta({ id: "InquiryCardData" });
const Party = { surface: "tenant", auth: "user" };
const InquiryId = z.object({ inquiryId: Id });
export const createInquiry = defineRoute({
    ...Party,
    permission: "inquiry:create",
    id: "trade.inquiries.create",
    method: "POST",
    path: "/v1/inquiries",
    marks: ["tenant", "verified", "idem"],
    summary: "Inquire on one or more public products of one shop: quantities of at least the minimum order, optional target prices in one currency, Incoterm, destination, target date, and a message. Opens the inquiry's deal conversation with the agent the shop's rule picks, and posts its card there",
    prd: ["INQ-1", "CAT-6"],
    request: {
        body: z.object({
            lines: z.array(InquiryLineInput).min(1).max(50),
            incoterm: Incoterm.optional(),
            destinationCountry: CountryCode.optional(),
            targetDate: z.iso.date().optional(),
            message: z.string().trim().min(1).max(2000).optional(),
        }),
    },
    responses: { 201: { description: "The inquiry", schema: InquiryView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "ENTERPRISE_NOT_VERIFIED",
        "ENTERPRISE_SUSPENDED",
        "VALIDATION_FAILED",
        "PRODUCT_UNAVAILABLE",
        "BELOW_MOQ",
        "INVALID_TRANSITION",
    ],
});
export const listInquiries = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.inquiries.list",
    method: "GET",
    path: "/v1/inquiries",
    marks: ["tenant"],
    summary: "The enterprise's inquiries as buyer, seller, or both, newest first, optionally in one status",
    prd: ["INQ-4"],
    request: { query: PageQuery.extend({ side: TradeSide.optional(), status: InquiryStatus.optional() }) },
    responses: { 200: { description: "Inquiries", schema: page(InquirySummary) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const getInquiry = defineRoute({
    ...Party,
    permission: "order:read",
    id: "trade.inquiries.get",
    method: "GET",
    path: "/v1/inquiries/{inquiryId}",
    marks: ["tenant"],
    summary: "One inquiry, with what the caller may do now",
    prd: ["INQ-4"],
    request: { params: InquiryId },
    responses: { 200: { description: "The inquiry", schema: InquiryView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const declineInquiry = defineRoute({
    ...Party,
    permission: "inquiry:respond",
    id: "trade.inquiries.decline",
    method: "POST",
    path: "/v1/inquiries/{inquiryId}/decline",
    marks: ["tenant", "idem"],
    summary: "The seller declines with a reason the buyer receives",
    prd: ["INQ-4"],
    request: { params: InquiryId, body: z.object({ reason: z.string().trim().min(3).max(500) }) },
    responses: { 200: { description: "The inquiry", schema: InquiryView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const withdrawInquiry = defineRoute({
    ...Party,
    permission: "inquiry:create",
    id: "trade.inquiries.withdraw",
    method: "POST",
    path: "/v1/inquiries/{inquiryId}/withdraw",
    marks: ["tenant", "idem"],
    summary: "The buyer withdraws the inquiry",
    prd: ["INQ-4"],
    request: { params: InquiryId },
    responses: { 200: { description: "The inquiry", schema: InquiryView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const inquiryRoutes = [
    createInquiry,
    listInquiries,
    getInquiry,
    declineInquiry,
    withdrawInquiry,
];
//# sourceMappingURL=inquiry.routes.js.map